import { useState, useMemo } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SearchInput } from '@/components/ui/search-input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { useHideAmounts } from '@/hooks/useHideAmounts';
import {
  KPICard,
  ChartTooltip,
  LoadingGrid,
  formatCurrency,
  formatCurrencyCompact,
  formatHours,
  COMPARE_COLORS,
} from './statisticsShared';
import type { StatisticsOutletContext } from './statisticsShared';

interface MatterStat {
  rank: number;
  id: number;
  name: string;
  group_name: string;
  status: string;
  billable: boolean;
  revenue: number;
  billable_hours: number;
  total_hours: number;
  entries: number;
  users_count: number;
  days_active: number;
  avg_rate: number;
  budget: number;
  budget_utilization: number;
  revenue_share: number;
  hours_share: number;
}

export default function MattersTab() {
  const { data, loading, hasFinancialData } = useOutletContext<StatisticsOutletContext>();
  const { hideAmounts, amountClassName } = useHideAmounts();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'revenue' | 'hours' | 'entries' | 'budget_utilization'>('revenue');
  const [groupFilter, setGroupFilter] = useState<string>('all');

  const matters: MatterStat[] = (data as any)?.matter_stats ?? [];

  // Get unique groups
  const groups = useMemo(() => {
    const set = new Set(matters.map((m) => m.group_name).filter(Boolean));
    return Array.from(set).sort();
  }, [matters]);

  // Filter and sort
  const filtered = useMemo(() => {
    let result = matters;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (m) =>
          m.name.toLowerCase().includes(q) ||
          m.group_name.toLowerCase().includes(q),
      );
    }
    if (groupFilter !== 'all') {
      result = result.filter((m) => m.group_name === groupFilter);
    }
    return [...result].sort((a, b) => {
      if (sortBy === 'revenue') return b.revenue - a.revenue;
      if (sortBy === 'hours') return b.billable_hours - a.billable_hours;
      if (sortBy === 'entries') return b.entries - a.entries;
      return b.budget_utilization - a.budget_utilization;
    });
  }, [matters, search, groupFilter, sortBy]);

  // Summary KPIs
  const totalRevenue = matters.reduce((s, m) => s + m.revenue, 0);
  const totalHours = matters.reduce((s, m) => s + m.billable_hours, 0);
  const totalEntries = matters.reduce((s, m) => s + m.entries, 0);
  const avgRate = matters.length > 0 ? matters.reduce((s, m) => s + m.avg_rate, 0) / matters.length : 0;
  const withBudget = matters.filter((m) => m.budget > 0);
  const avgBudgetUtil = withBudget.length > 0
    ? withBudget.reduce((s, m) => s + m.budget_utilization, 0) / withBudget.length
    : 0;

  // Top 10 for charts
  const top10 = filtered.slice(0, 10);

  // Client breakdown for pie chart (use top_clients from dashboard data)
  const clientBreakdown = useMemo(() => {
    const clients = (data as any)?.top_clients ?? [];
    return clients
      .filter((c: any) => (hasFinancialData ? (c.revenue ?? 0) > 0 : c.hours > 0))
      .map((c: any) => ({
        name: c.name,
        revenue: c.revenue ?? 0,
        hours: c.hours,
      }));
  }, [data, hasFinancialData]);

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KPICard
          title="Active Matters"
          value={String(matters.length)}
          subtitle="with activity this period"
          loading={loading}
        />
        {hasFinancialData && (
          <KPICard
            title="Total Revenue"
            value={hideAmounts ? '•••' : formatCurrency(totalRevenue)}
            loading={loading}
          />
        )}
        <KPICard
          title="Billable Hours"
          value={formatHours(totalHours)}
          subtitle={`${totalEntries} entries`}
          loading={loading}
        />
        {hasFinancialData && (
          <KPICard
            title="Avg Rate"
            value={hideAmounts ? '•••' : formatCurrency(avgRate)}
            subtitle="across all matters"
            loading={loading}
          />
        )}
        <KPICard
          title="Avg Budget Usage"
          value={`${avgBudgetUtil.toFixed(0)}%`}
          subtitle={`${withBudget.length} matters with budget`}
          loading={loading}
        />
      </div>

      {/* Charts row */}
      {!loading && matters.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top 10 by revenue/hours */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                Top 10 Matters by {hasFinancialData ? 'Revenue' : 'Hours'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  layout="vertical"
                  data={top10.map((m) => ({
                    name: m.name.length > 25 ? m.name.slice(0, 25) + '…' : m.name,
                    value: hasFinancialData ? m.revenue : m.billable_hours,
                    hours: m.billable_hours,
                    revenue: m.revenue,
                  }))}
                  margin={{ top: 5, right: 20, bottom: 5, left: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="hsl(var(--border))" />
                  <XAxis
                    type="number"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) =>
                      hasFinancialData && !hideAmounts ? formatCurrencyCompact(v) : `${v.toFixed(0)}h`
                    }
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={160}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip
                    content={<ChartTooltip format={hasFinancialData && !hideAmounts ? 'currency' : 'hours'} />}
                  />
                  <Bar
                    dataKey="value"
                    fill="#6366f1"
                    radius={[0, 4, 4, 0]}
                    maxBarSize={24}
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Client breakdown pie chart */}
          {clientBreakdown.length > 1 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">
                  {hasFinancialData ? 'Revenue' : 'Hours'} by Client
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={clientBreakdown}
                      dataKey={hasFinancialData ? 'revenue' : 'hours'}
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ name, percent }: { name?: string; percent?: number }) => {
                        if (!percent || percent < 0.04 || !name) return null;
                        const label = name.length > 15 ? name.slice(0, 15) + '…' : name;
                        return `${label} (${(percent * 100).toFixed(0)}%)`;
                      }}
                      labelLine={false}
                    >
                      {clientBreakdown.map((_: any, i: number) => (
                        <Cell key={i} fill={COMPARE_COLORS[i % COMPARE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) =>
                        hasFinancialData && !hideAmounts ? formatCurrency(Number(value)) : formatHours(Number(value))
                      }
                    />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Filters + Table */}
      {!loading && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <CardTitle className="text-base">All Matters</CardTitle>
                <CardDescription>{filtered.length} matters</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <SearchInput
                  placeholder="Search matters…"
                  value={search}
                  onChange={setSearch}
                  className="w-56"
                />
                {groups.length > 0 && (
                  <Select value={groupFilter} onValueChange={setGroupFilter}>
                    <SelectTrigger className="w-44 h-9">
                      <SelectValue placeholder="All groups" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All groups</SelectItem>
                      {groups.map((g) => (
                        <SelectItem key={g} value={g}>
                          {g}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Select value={sortBy} onValueChange={(v: any) => setSortBy(v)}>
                  <SelectTrigger className="w-40 h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {hasFinancialData && <SelectItem value="revenue">Sort by Revenue</SelectItem>}
                    <SelectItem value="hours">Sort by Hours</SelectItem>
                    <SelectItem value="entries">Sort by Entries</SelectItem>
                    <SelectItem value="budget_utilization">Sort by Budget %</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8">#</TableHead>
                    <TableHead>Group</TableHead>
                    <TableHead>Matter</TableHead>
                    <TableHead className="text-right">Billable Hours</TableHead>
                    <TableHead className="text-right">Total Hours</TableHead>
                    {hasFinancialData && <TableHead className="text-right">Revenue</TableHead>}
                    {hasFinancialData && <TableHead className="text-right">Avg Rate</TableHead>}
                    <TableHead className="text-right">Entries</TableHead>
                    <TableHead className="text-right">Team</TableHead>
                    <TableHead className="text-right">Budget</TableHead>
                    <TableHead className="text-right">Budget %</TableHead>
                    <TableHead className="text-right">Share</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((m) => (
                    <TableRow
                      key={m.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/matters/detail/${m.id}`)}
                    >
                      <TableCell className="text-muted-foreground">{m.rank}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {m.group_name || '—'}
                      </TableCell>
                      <TableCell className="font-medium max-w-[250px] truncate">
                        {m.name}
                        {!m.billable && (
                          <Badge variant="outline" className="ml-2 text-[10px] py-0">
                            Non-billable
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">{m.billable_hours.toFixed(1)}h</TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {m.total_hours.toFixed(1)}h
                      </TableCell>
                      {hasFinancialData && (
                        <TableCell className={`text-right font-semibold ${amountClassName}`}>
                          {hideAmounts ? '•••' : formatCurrency(m.revenue)}
                        </TableCell>
                      )}
                      {hasFinancialData && (
                        <TableCell className={`text-right ${amountClassName}`}>
                          {hideAmounts ? '•••' : formatCurrency(m.avg_rate)}
                        </TableCell>
                      )}
                      <TableCell className="text-right">{m.entries}</TableCell>
                      <TableCell className="text-right">{m.users_count}</TableCell>
                      <TableCell className={`text-right ${amountClassName}`}>
                        {m.budget > 0
                          ? hideAmounts
                            ? '•••'
                            : formatCurrency(m.budget)
                          : '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        {m.budget > 0 ? (
                          <span
                            className={
                              m.budget_utilization > 100
                                ? 'text-rose-600 font-semibold'
                                : m.budget_utilization > 80
                                  ? 'text-amber-600'
                                  : 'text-emerald-600'
                            }
                          >
                            {m.budget_utilization.toFixed(0)}%
                          </span>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {hasFinancialData
                          ? `${m.revenue_share.toFixed(1)}%`
                          : `${m.hours_share.toFixed(1)}%`}
                      </TableCell>
                    </TableRow>
                  ))}
                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={hasFinancialData ? 12 : 10}
                        className="text-center text-muted-foreground py-8"
                      >
                        No matters found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {loading && (
        <>
          <LoadingGrid count={5} />
          <Card>
            <CardContent className="pt-6">
              <div className="h-[300px] bg-muted/30 rounded animate-pulse" />
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
